import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Logo } from "@/components/domain/Bits";
import { useTenant } from "@/context/TenantContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { ShieldCheck, Menu, Search, Trophy, FileCheck2, HelpCircle, Phone, Ticket, Building2 } from "lucide-react";

const NAV = [
  { to: "/sorteos", label: "Sorteos" },
  { to: "/ganadores", label: "Ganadores" },
  { to: "/encuesta", label: "Vota el premio" },
  { to: "/transparencia", label: "Transparencia" },
  { to: "/nosotros", label: "Sobre nosotros" },
  { to: "/como-participar", label: "Cómo participar" },
  { to: "/faq", label: "Preguntas" },
];

const TRUST = [
  "Sorteos documentados", "Ganadores verificables", "Pagos identificados con folio",
  "Resultados públicos", "Cuentas bancarias oficiales", "Atención por canales verificados",
];

function TrustBar() {
  return (
    <div className="w-full overflow-hidden bg-black py-2 text-white">
      <div className="flex animate-[marquee_28s_linear_infinite] gap-10 whitespace-nowrap px-6 text-xs font-medium">
        {[...TRUST, ...TRUST].map((t, i) => (
          <span key={i} className="inline-flex items-center gap-2 text-slate-300">
            <ShieldCheck className="h-3.5 w-3.5 text-brand-bright" /> {t}
          </span>
        ))}
      </div>
      <style>{`@keyframes marquee{from{transform:translateX(0)}to{transform:translateX(-50%)}}`}</style>
    </div>
  );
}

export default function PublicLayout({ children }) {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  return (
    <div className="min-h-screen serif-scope bg-[#FBF9F3]">
      <TrustBar />
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
          <Link to="/" data-testid="nav-home"><Logo /></Link>
          <nav className="hidden items-center gap-1 lg:flex">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.label}`}
                className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:bg-slate-100 ${
                  loc.pathname.startsWith(n.to) ? "text-brand" : "text-slate-600"}`}>
                {n.label}
              </Link>
            ))}
          </nav>
          <div className="hidden items-center gap-2 lg:flex">
            <Button asChild variant="ghost" size="sm" data-testid="nav-lookup">
              <Link to="/consulta"><Search className="mr-1.5 h-4 w-4" />Consultar folio</Link>
            </Button>
            <Button asChild size="sm" className="rounded-full bg-brand hover:bg-brand-hover" data-testid="nav-participate">
              <Link to="/sorteos"><Ticket className="mr-1.5 h-4 w-4" />Participar</Link>
            </Button>
          </div>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80">
              <div className="mb-6"><Logo /></div>
              <nav className="flex flex-col gap-1">
                {NAV.map((n) => (
                  <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
                    className="rounded-lg px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100">{n.label}</Link>
                ))}
                <Link to="/consulta" onClick={() => setOpen(false)} className="rounded-lg px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100">Consultar folio</Link>
                <Link to="/sala" onClick={() => setOpen(false)} className="rounded-lg px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100">Sala del sorteo</Link>
              </nav>
              <Button asChild className="mt-6 w-full rounded-full bg-brand hover:bg-brand-hover">
                <Link to="/sorteos" onClick={() => setOpen(false)}>Participar ahora</Link>
              </Button>
              <Link to="/admin" onClick={() => setOpen(false)} className="mt-4 block text-center text-xs text-slate-400">Acceso operadores</Link>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <main>{children}</main>

      <footer className="mt-24 border-t border-slate-200 bg-white">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-14 sm:px-8 lg:grid-cols-4">
          <div>
            <Logo />
            <p className="mt-4 max-w-xs text-sm text-slate-500">Plataforma de sorteos con transparencia verificable. La confianza vende antes que el premio.</p>
            <div className="mt-4 flex items-center gap-2 text-xs text-emerald-600"><ShieldCheck className="h-4 w-4" />Operación documentada y auditada</div>
          </div>
          <FooterCol title="Explorar" links={[["/sorteos", "Sorteos activos"], ["/ganadores", "Ganadores"], ["/como-participar", "Cómo participar"], ["/consulta", "Consultar folio"]]} />
          <FooterCol title="Confianza" links={[["/transparencia", "Centro de Transparencia"], ["/nosotros", "Sobre nosotros"], ["/faq", "Preguntas frecuentes"], ["/contacto", "Contacto"]]} />
          <FooterCol title="Operadores" links={[["/admin", "Panel de administración"], ["/vendedor", "Portal del vendedor"], ["/plataforma", "Administración de la plataforma"]]} />
        </div>
        <div className="border-t border-slate-100 py-6">
          <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-2 px-5 text-xs text-slate-400 sm:flex-row sm:px-8">
            <span>© 2026 Círculo Dorado S.A. de C.V. · Datos de demostración.</span>
            <span className="flex items-center gap-4">
              <Link to="/transparencia" className="hover:text-slate-600">Bases legales</Link>
              <Link to="/transparencia" className="hover:text-slate-600">Aviso de privacidad</Link>
            </span>
          </div>
        </div>
      </footer>
      <WhatsAppFab />
    </div>
  );
}

const WhatsAppFab = () => {
  const data = useTenant();
  const phone = (data?.tenant?.whatsapp || "").replace(/\D/g, "");
  if (!phone) return null;
  const msg = encodeURIComponent(`Hola, me interesa información sobre los sorteos de ${data.tenant.name}.`);
  return (
    <a href={`https://wa.me/${phone}?text=${msg}`} target="_blank" rel="noopener noreferrer" aria-label="Escríbenos por WhatsApp"
      data-testid="whatsapp-fab"
      className="fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg shadow-emerald-900/30 transition-transform hover:scale-110 active:scale-95">
      <svg viewBox="0 0 32 32" className="h-7 w-7 fill-current" aria-hidden="true">
        <path d="M16 3C9.4 3 4 8.3 4 14.9c0 2.3.7 4.5 1.9 6.4L4 29l7.9-1.8c1.8.9 3.8 1.4 5.9 1.4h.2c6.6 0 12-5.3 12-11.9C30 8.3 24.6 3 16 3zm0 22.6c-1.9 0-3.7-.5-5.3-1.4l-.4-.2-4.6 1 1.1-4.4-.3-.4c-1.1-1.7-1.7-3.6-1.7-5.6C4.8 9.2 9.8 4.9 16 4.9s11.2 4.3 11.2 9.9S22.2 25.6 16 25.6zm5.8-7.4c-.3-.2-1.9-.9-2.2-1-.3-.1-.5-.2-.7.2-.2.3-.8 1-1 1.2-.2.2-.4.2-.7.1-.3-.2-1.3-.5-2.5-1.6-.9-.8-1.6-1.9-1.8-2.2-.2-.3 0-.5.1-.7l.6-.7c.2-.2.2-.4.3-.6.1-.2 0-.4 0-.6-.1-.2-.7-1.7-1-2.3-.3-.6-.5-.5-.7-.5h-.6c-.2 0-.6.1-.9.4-.3.3-1.2 1.1-1.2 2.8s1.2 3.3 1.4 3.5c.2.2 2.4 3.6 5.8 5 .8.3 1.4.6 1.9.7.8.3 1.5.2 2.1.1.6-.1 1.9-.8 2.2-1.6.3-.8.3-1.4.2-1.6-.1-.1-.3-.2-.6-.4z"/>
      </svg>
    </a>
  );
};

const FooterCol = ({ title, links }) => (
  <div>
    <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
    <ul className="mt-3 space-y-2">
      {links.map(([to, l]) => (
        <li key={to}><Link to={to} className="text-sm text-slate-500 hover:text-brand">{l}</Link></li>
      ))}
    </ul>
  </div>
);
